/**
 * Tiny date-formatter helpers used by ClarifyEntryCard + ClarifyActionPanel.
 * Keep the scope local — there is no app-wide relative-date utility to reuse.
 */

// Intl.RelativeTimeFormat allocates a substantial amount per locale lookup —
// cache one instance per locale so the formatter isn't rebuilt per call.
const relativeTimeFormatters = new Map<string, Intl.RelativeTimeFormat>()
function getRelativeTimeFormatter(locale: string): Intl.RelativeTimeFormat {
  let rtf = relativeTimeFormatters.get(locale)
  if (!rtf) {
    rtf = new Intl.RelativeTimeFormat(locale, { numeric: 'auto', style: 'long' })
    relativeTimeFormatters.set(locale, rtf)
  }
  return rtf
}

/**
 * "vor 1 Tag" / "vor 3 Tagen" / "heute" / "vor 2 Stunden" — uses the native
 * Intl.RelativeTimeFormat so localisation comes for free.
 */
export function formatRelativeAge(date: Date, locale = 'de'): string {
  const rtf = getRelativeTimeFormatter(locale)
  const diffMs = date.getTime() - Date.now()
  const diffDays = Math.round(diffMs / (1000 * 60 * 60 * 24))
  if (Math.abs(diffDays) >= 1) return rtf.format(diffDays, 'day')
  const diffHours = Math.round(diffMs / (1000 * 60 * 60))
  if (Math.abs(diffHours) >= 1) return rtf.format(diffHours, 'hour')
  const diffMinutes = Math.round(diffMs / (1000 * 60))
  return rtf.format(diffMinutes, 'minute')
}

/**
 * Short-weekday + day + long-month, e.g. "Fr. 9. Januar" / "Fri, 9 January".
 * Used on staged-inline cards when the user picked a custom "Demnächst" date.
 */
export function formatShortDate(date: Date, locale = 'de'): string {
  return date.toLocaleDateString(locale, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
  })
}
